<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesDetailSignatureSigner from './SalesDetailSignatureSigner.vue';

const props = defineProps({
  integration: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  canResend: { type: Boolean, default: false },
  resendingSignerReference: { type: String, default: '' },
});

const emit = defineEmits(['refresh', 'resendSigner']);
const { t } = useI18n();
let refreshTimer;

const pending = computed(() => props.integration.pending_signers || []);
const signed = computed(() => props.integration.signed_signers || []);
const hasEnvelopeData = computed(() =>
  Boolean(
    props.integration.sent_at || pending.value.length || signed.value.length
  )
);
const shouldRefresh = computed(() =>
  ['sent', 'partially_signed'].includes(props.integration.status)
);

const formatMoment = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const dateLabel = new Intl.DateTimeFormat('pt-BR').format(date);
  const timeLabel = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(':', 'h');

  return `${dateLabel}, ${timeLabel}`;
};

const signerEventLabel = (signer, date) =>
  [
    formatMoment(date),
    signer.display_name || t('SALES.DETAIL.RECEIPT.DOCUSIGN_SIGNER'),
  ]
    .filter(Boolean)
    .join(' - ');

const clearRefreshTimer = () => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = null;
};

const scheduleRefresh = () => {
  clearRefreshTimer();
  if (!shouldRefresh.value) return;

  refreshTimer = setInterval(() => emit('refresh'), 60000);
};

onMounted(() => {
  emit('refresh');
  scheduleRefresh();
});

watch(shouldRefresh, scheduleRefresh);
onBeforeUnmount(clearRefreshTimer);
</script>

<template>
  <div class="grid gap-4">
    <p
      v-if="loading"
      class="flex items-center gap-2 text-sm text-lais-text-muted"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      {{ t('SALES.DETAIL.RECEIPT.DOCUSIGN_LOADING') }}
    </p>

    <ol v-else-if="hasEnvelopeData" class="grid gap-3">
      <li v-if="integration.sent_at" class="flex gap-3 text-sm text-lais-text">
        <span class="i-lucide-circle-check size-6 shrink-0 text-lais-success" />
        <span>
          <strong>{{ formatMoment(integration.sent_at) }}</strong>
          <span class="ms-1">
            {{ t('SALES.DETAIL.RECEIPT.DOCUSIGN_EMAILS_SENT') }}
          </span>
        </span>
      </li>

      <li v-if="pending.length" class="flex gap-3 text-sm text-lais-text-muted">
        <span
          class="mt-1 size-3 shrink-0 animate-pulse rounded-full bg-lais-warning"
        />
        <span>{{ t('SALES.DETAIL.RECEIPT.DOCUSIGN_REVIEWING') }}</span>
      </li>

      <SalesDetailSignatureSigner
        v-for="(signer, index) in signed"
        :key="`signed-${signer.display_name}-${index}`"
        :signer="signer"
        signed
        :event-label="
          signerEventLabel(signer, signer.signed_at || signer.sent_at)
        "
        :status-label="t('SALES.DETAIL.RECEIPT.DOCUSIGN_SIGNED')"
      />

      <SalesDetailSignatureSigner
        v-for="(signer, index) in pending"
        :key="
          signer.signer_reference || `pending-${signer.display_name}-${index}`
        "
        :signer="signer"
        :event-label="signerEventLabel(signer, signer.sent_at)"
        :status-label="t('SALES.DETAIL.RECEIPT.DOCUSIGN_PENDING')"
        :can-resend="canResend && Boolean(signer.signer_reference)"
        :is-resending="resendingSignerReference === signer.signer_reference"
        @resend="emit('resendSigner', $event)"
      />
    </ol>

    <p v-else class="text-sm text-lais-text-muted">
      {{ t('SALES.DETAIL.RECEIPT.DOCUSIGN_NO_DATA') }}
    </p>
  </div>
</template>
